import { ADDRESS, TELEPHONE } from '@iva/utils';
import { IDENTITIES_KEYS, IDENTITIES_VALUES_TYPES, IDENTITY_STATE, VALUES_TYPES, } from '../types';
import { createEmailHash } from './externals/eid';
import { createHash } from './hash';
import { storeInvalidUpdates } from './invalidUpdates';
import { castBoolean, isObject, isValidSHA256String, isValidUUID } from './utils';
import { debug, sendToConfessor } from './debug';
// type guard IDENTITY_OBJECT vs IDENTITY_VALUE
export const isIdentityObject = (data) => {
    try {
        return (data.value !== undefined &&
            data.created !== undefined &&
            data.updated !== undefined &&
            data.state !== undefined &&
            data.settled !== undefined);
    }
    catch (_a) {
        return false;
    }
};
/**
 * Do we have valid object with at least one identitity set?
 */
export const identitiesNotEmpty = (identities) => {
    return !!(isObject(identities) && Object.keys(identities).length);
};
/**
 * Extract just values for each identity
 */
export const getIdentitiesValues = (identities) => {
    const output = {};
    for (const key in identities) {
        const identityKey = key;
        const identityItem = identities[identityKey];
        if (isIdentityObject(identityItem)) {
            output[identityKey] = identityItem.value;
        }
        else {
            output[identityKey] = identityItem;
        }
    }
    return output;
};
/**
 * Is identificator valid? (Is it out of scope of this lib?).
 */
export const validateIdentificator = (identificator) => {
    if (typeof identificator !== 'string') {
        return false;
    }
    const identificatorSplit = identificator.split('.');
    return identificatorSplit[0] in IDENTITIES_KEYS;
};
/**
 * Helpers for value validation
 */
// return OK + value
const handleOkValue = (value) => ({ ok: true, value });
// return NOK + store to "_invalidIdentities"
const handleInvalidValue = (identificator, value) => {
    storeInvalidUpdates({
        identity: identificator,
        value,
        type: 'invalid',
        timestamp: Date.now(),
    });
    debug({ type: 'warn', message: `Submitted value "${value}" for identity "${identificator}" is invalid.` });
    return { ok: false };
};
// check casted values equality
const handleCastedValue = (value, castedValue, identificator) => {
    try {
        // eslint-disable-next-line eqeqeq
        if (value == castedValue) {
            return handleOkValue(castedValue);
        }
    }
    catch (_a) {
        // no-op
    }
    return handleInvalidValue(identificator, value);
};
/**
 * Is value type valid? If it is "translatable" ("1234" to 1234) -> do it
 */
export const validateValueType = (identificator, value) => {
    const expectedType = IDENTITIES_VALUES_TYPES[identificator];
    // clearing -> allow NULL value
    if (value === null) {
        return handleOkValue(value);
    }
    // STRING vs INT
    if (expectedType === VALUES_TYPES.integer && typeof value === VALUES_TYPES.string) {
        return handleCastedValue(value, parseInt(value, 10), identificator);
    }
    // INT / STRING vs BOOLEAN
    if (expectedType === VALUES_TYPES.boolean && typeof value !== VALUES_TYPES.boolean) {
        const casted = castBoolean(value);
        return handleCastedValue(casted !== null && casted !== void 0 ? casted : value, casted, identificator);
    }
    try {
        // address
        if (expectedType === VALUES_TYPES.addressObject) {
            const validatedAddress = ADDRESS.createAddressObject(value);
            return Object.keys(validatedAddress).length > 0
                ? handleOkValue(validatedAddress)
                : handleInvalidValue(identificator, value);
        }
        // phone number
        if (expectedType === VALUES_TYPES.phone) {
            const validatedPhone = TELEPHONE.isValidHashedTidObject(value) ? value : TELEPHONE.createPhoneObject(value);
            return Object.keys(validatedPhone).length > 0
                ? handleOkValue(validatedPhone)
                : handleInvalidValue(identificator, value);
        }
        // email -> handle hash or plaintext
        if (expectedType === VALUES_TYPES.email) {
            const validatedEmail = isValidSHA256String(value) && !isHashBlacklisted(value) ? value : createEmailHash(value);
            return validatedEmail ? handleOkValue(validatedEmail) : handleInvalidValue(identificator, value);
        }
    }
    catch (e) {
        debug('error', e);
        if (e instanceof Error) {
            const { message, stack } = e;
            sendToConfessor(message, 'error', stack);
        }
        return handleInvalidValue(identificator, value);
    }
    // secid -> handle uuid
    if (expectedType === VALUES_TYPES.secid) {
        return isValidUUID(value) ? handleOkValue(value) : handleInvalidValue(identificator, value);
    }
    // non valid -> ignored
    if ((expectedType === VALUES_TYPES.string && typeof value !== VALUES_TYPES.string) ||
        (expectedType === VALUES_TYPES.array && !Array.isArray(value)) ||
        (expectedType === VALUES_TYPES.object && (typeof value !== VALUES_TYPES.object || Array.isArray(value))) ||
        (expectedType === VALUES_TYPES.hashString && !isValidSHA256String(value))) {
        return handleInvalidValue(identificator, value);
    }
    return handleOkValue(value);
};
/**
 * Generate value object (add timestamps, ...)
 */
export const createIdentityObject = (storageItem, value, state = IDENTITY_STATE.fresh, settled = false) => {
    return {
        value,
        created: (storageItem === null || storageItem === void 0 ? void 0 : storageItem.created) || Date.now(),
        updated: (settled && (storageItem === null || storageItem === void 0 ? void 0 : storageItem.state) === IDENTITY_STATE.fresh) || !(storageItem === null || storageItem === void 0 ? void 0 : storageItem.updated)
            ? Date.now()
            : storageItem.updated,
        state,
        settled,
    };
};
/**
 * Identity helpers
 */
// value extractor
export const identityOutput = (identity, rich = false) => (rich ? identity : identity.value);
// state validator
export const isStateValid = (identity, cacheEnough = false) => (identity === null || identity === void 0 ? void 0 : identity.state) === IDENTITY_STATE.fresh || cacheEnough;
// "get" rules validator
export const isOK = (identity, cacheEnough = false, justSettled = true) => identity && (!justSettled || (identity === null || identity === void 0 ? void 0 : identity.settled)) && isStateValid(identity, cacheEnough);
// blacklisted "no meaning" hashes
export const isHashBlacklisted = (hash) => [createHash('@'), createHash('undefined'), createHash(''), createHash('null'), createHash('unknown')].includes(hash);
